import type { AxiosError } from 'axios';
import type { ProductSummary } from '@/contexts/ProductsContext';

/** Integração mínima exigida para carregar dados da tela. */
export type ProductIntegrationNeed = 'appApi' | 'billing' | 'core';

export type ProductConnectionTest = {
  productId: string;
  billing: { ok: boolean; detail?: string };
  appApi: { ok: boolean; detail?: string; attemptedUrl?: string; secretConfigured?: boolean };
};

export type ParsedLoadError = {
  kind: 'misconfigured' | 'unreachable' | 'forbidden' | 'unknown';
  title: string;
  detail: string;
};

export function syncRegistryMisconfig(
  product: ProductSummary | undefined,
  need: ProductIntegrationNeed
): ParsedLoadError | null {
  if (need === 'core') return null;
  if (!product) {
    return {
      kind: 'misconfigured',
      title: 'Produto não encontrado no registry',
      detail: 'Cadastre o produto em Produtos > Produtos antes de continuar.'
    };
  }
  if (need === 'appApi') {
    const url = product.urls?.appApi?.trim();
    const secretConfigured = product.urls?.appApiSecretConfigured === true;
    if (!url) {
      return {
        kind: 'misconfigured',
        title: 'API interna do produto não configurada',
        detail: 'Defina a URL em urls.appApi no registry do produto (aba Produtos → editar).'
      };
    }
    if (!secretConfigured) {
      return {
        kind: 'misconfigured',
        title: 'Secret da API interna não configurado',
        detail:
          'Defina urls.appApiSecret no registry (mesmo valor do secret no backend do produto, ex.: VOVER_APP_API_SECRET).'
      };
    }
  }
  if (need === 'billing' && product.integrations?.billingEnabled !== false) {
    return null;
  }
  return null;
}

export function connectionMisconfig(
  test: ProductConnectionTest,
  need: ProductIntegrationNeed
): ParsedLoadError | null {
  if (need === 'appApi' && !test.appApi.ok) {
    if (!test.appApi.secretConfigured && test.appApi.detail?.includes('Secret')) {
      return {
        kind: 'misconfigured',
        title: 'API interna do produto não configurada',
        detail: test.appApi.detail || 'Secret ou URL ausente no registry.'
      };
    }
    if (!test.appApi.attemptedUrl) {
      return {
        kind: 'misconfigured',
        title: 'API interna do produto não configurada',
        detail: test.appApi.detail || 'URL da API não definida.'
      };
    }
    return {
      kind: 'unreachable',
      title: 'API do produto indisponível',
      detail:
        test.appApi.detail ||
        (test.appApi.attemptedUrl
          ? `Não foi possível alcançar ${test.appApi.attemptedUrl}. Verifique se o backend do produto está no ar.`
          : 'O health check da API interna falhou.')
    };
  }
  if (need === 'billing' && !test.billing.ok) {
    return {
      kind: 'misconfigured',
      title: 'Integração com vover-billing não configurada',
      detail:
        test.billing.detail ||
        'Configure VOVER_BILLING_URL e VOVER_BILLING_API_KEY no vover-admin, ou desative billing no registry do produto.'
    };
  }
  return null;
}

export function parseLoadError(err: unknown): ParsedLoadError {
  const ax = err as AxiosError<{ message?: string; detail?: string; title?: string }>;
  const status = ax.response?.status;
  const body = ax.response?.data;
  const detail =
    (typeof body?.detail === 'string' && body.detail) ||
    (typeof body?.message === 'string' && body.message) ||
    (err instanceof Error ? err.message : '') ||
    'Erro desconhecido ao carregar dados.';

  if (status === 403) {
    return {
      kind: 'forbidden',
      title: 'Sem permissão para este produto',
      detail
    };
  }
  if (status === 404) {
    return {
      kind: 'misconfigured',
      title: 'Recurso não encontrado para este produto',
      detail
    };
  }
  if (status === 503 || status === 502 || status === 504) {
    const billingHint =
      detail.includes('VOVER_BILLING') || detail.includes('Billing')
        ? 'Integração com vover-billing indisponível ou incompleta.'
        : '';
    return {
      kind: status === 503 ? 'misconfigured' : 'unreachable',
      title: status === 503 ? 'Serviço indisponível' : 'Serviço não respondeu',
      detail: billingHint ? `${billingHint} ${detail}` : detail
    };
  }
  if (!ax.response) {
    return {
      kind: 'unreachable',
      title: 'Falha de comunicação',
      detail:
        'Não foi possível contactar o servidor. Verifique rede e se os serviços estão em execução.'
    };
  }
  return {
    kind: 'unknown',
    title: 'Erro ao carregar dados',
    detail
  };
}
